import interact from 'interactjs'

export default {
  data() {
    return {
      layoutEditing: false, /* 默认保持浏览模式，点击编辑布局后才允许拖动 */
      freeLayoutActive: false, /* 当前区域是否使用自由坐标布局 */
      customLayoutAreas: {}, /* 记录各区域是否存在已经保存的自由布局 */
      shelfCanvasHeight: 650, /* 自由布局画布高度，切换区域时按货架数量和已保存坐标计算 */
      shelfInteractables: [], /* interact.js 实例，页面更新或销毁时统一释放 */
    }
  },
  beforeDestroy() {
    this.destroyShelfDragging()
  },
  methods: {
    /**
     * 返回货架的稳定标识，用于 Vue 渲染、拖动定位和本地布局持久化。
     * 接口有货架 ID 时优先使用 ID，否则使用页面现有的区域编号。
     * @param {Object} shelf 货架数据
     * @returns {String} 货架稳定标识
     */
    getShelfKey(shelf) {
      return String(shelf.id || shelf.area_number)
    },
    /**
     * 获取当前登录操作人的 ID，使不同操作人的本地货架布局互不影响。
     * 登录信息异常或缺少 ID 时使用 default 作为兜底命名空间。
     * @returns {String} 当前登录操作人 ID
     */
    getLoginUserId() {
      try {
        const userInfo = JSON.parse(localStorage.getItem('japanUserRoleInfo') || '{}')
        return String(userInfo.id || 'default')
      } catch (error) {
        return 'default'
      }
    },
    /**
     * 生成布局存储键，按照登录人、公共/个人区域和货架类型隔离布局。
     * @param {String|Number} area 区域标识
     * @returns {String} localStorage 存储键
     */
    getShelfLayoutStorageKey(area) {
      const shelfTypes = (this.shelf_type_set || []).slice().sort().join(',') || 'all'
      return `warehouseShelfLayout:v1:${this.getLoginUserId()}:${area}:${shelfTypes}`
    },
    /**
     * 读取指定区域保存的自由布局；无数据或数据损坏时返回空对象。
     * @param {String|Number} area 区域标识
     * @returns {Object} 以货架标识为键的坐标和层级数据
     */
    readShelfLayout(area) {
      try {
        const layout = JSON.parse(localStorage.getItem(this.getShelfLayoutStorageKey(area)) || '{}')
        return layout.positions || {}
      } catch (error) {
        return {}
      }
    },
    /**
     * 按原来的三列视觉结构为未保存位置的货架生成默认坐标。
     * @param {Number} index 货架在接口列表中的索引
     * @returns {Object} 默认 x、y、宽度和 zIndex
     */
    getDefaultShelfLayout(index) {
      const column = index % 3
      const row = Math.floor(index / 3)
      return {
        x: column * 570,
        y: row * 360,
        width: 506,
        zIndex: index + 1,
      }
    },
    /**
     * 为指定区域的货架加载已保存位置，并为新增货架补充默认位置。
     * @param {String|Number} area 区域标识
     * @param {Boolean} reset 是否忽略已保存布局并恢复默认位置
     */
    initializeShelfLayouts(area, reset = false) {
      const shelves = this.warehouseData[area] || []
      const savedLayout = reset ? {} : this.readShelfLayout(area)
      this.$set(this.customLayoutAreas, String(area), Object.keys(savedLayout).length > 0)
      shelves.forEach((shelf, index) => {
        const saved = savedLayout[this.getShelfKey(shelf)]
        const layout = saved || this.getDefaultShelfLayout(index)
        this.$set(shelf, '_featureLayout', {
          x: Number(layout.x) || 0,
          y: Number(layout.y) || 0,
          width: Math.max(506, Number(layout.width) || 506),
          zIndex: Number(layout.zIndex) || index + 1,
        })
      })
    },
    /**
     * 生成货架的绝对定位样式和自定义宽度。坐标独立于 list 数组顺序，因此货架可以自由重叠。
     * @param {Object} shelf 货架数据
     * @returns {Object} Vue 行内样式对象
     */
    shelfPositionStyle(shelf) {
      if (!this.freeLayoutActive) return {}
      const layout = shelf._featureLayout || { x: 0, y: 0, width: 506, zIndex: 1 }
      return {
        transform: `translate(${layout.x}px, ${layout.y}px)`,
        width: `${Math.max(506, layout.width || 506)}px`,
        zIndex: layout.zIndex,
      }
    },
    /**
     * 根据当前区域的货架数量和已有坐标设置固定画布高度。
     * 高度不会在拖动过程中跟随坐标无限增长，货架会被限制在当前画布范围内。
     * @param {String|Number} area 区域标识
     */
    updateShelfCanvasHeight(area) {
      const shelves = this.warehouseData[area] || []
      const defaultHeight = Math.ceil(shelves.length / 3) * 360 + 40
      const savedHeight = shelves.reduce((height, shelf) => {
        const y = shelf._featureLayout ? shelf._featureLayout.y : 0
        return Math.max(height, y + 420)
      }, 0)
      this.shelfCanvasHeight = Math.max(650, defaultHeight, savedHeight)
    },
    /**
     * 进入编辑模式时将原始浮动排版转换为等效坐标；再次点击时保存布局并结束编辑。
     * 已保存过自由布局的区域会直接在现有坐标上继续编辑。
     */
    toggleLayoutEditing() {
      if (!this.layoutEditing) {
        if (!this.freeLayoutActive) {
          this.captureDefaultShelfPositions()
          this.updateShelfCanvasHeight(this.i)
          this.freeLayoutActive = true
        }
        this.layoutEditing = true
        this.$nextTick(this.setupShelfDragging)
        return
      }
      this.saveShelfLayout(this.i)
      this.layoutEditing = false
      this.destroyShelfDragging()
      this.$message.success('布局已保存')
    },
    /**
     * 读取原始浮动排版中每个货架的实际位置，并转换成自由布局坐标。
     * 因为读取的是浏览器已经完成排版后的 offset，所以进入编辑时不会发生位置跳动。
     */
    captureDefaultShelfPositions() {
      if (!this.$refs.shelfCanvas) return
      const elements = this.$refs.shelfCanvas.querySelectorAll('.warehouse-feature-shelf')
      Array.from(elements).forEach((element) => {
        const shelf = this.findShelfByElement(element)
        if (!shelf) return
        this.$set(shelf, '_featureLayout', {
          x: element.offsetLeft,
          y: element.offsetTop,
          width: Math.max(506, element.offsetWidth),
          zIndex: shelf._featureLayout ? shelf._featureLayout.zIndex : 1,
        })
      })
    },
    /**
     * 清除当前区域已保存的坐标，并恢复默认三列布局。
     */
    resetShelfLayout() {
      localStorage.removeItem(this.getShelfLayoutStorageKey(this.i))
      this.initializeShelfLayouts(this.i, true)
      this.$set(this.customLayoutAreas, String(this.i), false)
      this.layoutEditing = false
      this.freeLayoutActive = false
      this.updateShelfCanvasHeight(this.i)
      this.destroyShelfDragging()
      this.$message.success('已恢复默认布局')
    },
    /**
     * 为当前画布中可见的货架绑定 interact.js 拖动和横向缩放行为。
     * 仅拖动名称把手；货架右侧可以拉宽，并通过限制器保证留在画布范围内。
     */
    setupShelfDragging() {
      this.destroyShelfDragging()
      if (!this.layoutEditing || !this.$refs.shelfCanvas) return
      const elements = this.$refs.shelfCanvas.querySelectorAll('.warehouse-feature-shelf')
      this.shelfInteractables = Array.from(elements).map((element) => {
        const interactable = interact(element)
        interactable.draggable({
          allowFrom: '.drag-handle',
          autoScroll: true,
          modifiers: [
            interact.modifiers.restrictRect({
              restriction: 'parent',
            }),
          ],
          listeners: {
            start: this.onShelfDragStart,
            move: this.onShelfDragMove,
          },
        })
        interactable.resizable({
          edges: {
            right: true,
          },
          modifiers: [
            interact.modifiers.restrictSize({
              min: {
                width: 506,
              },
            }),
            interact.modifiers.restrictEdges({
              outer: 'parent',
            }),
          ],
          listeners: {
            start: this.onShelfDragStart,
            move: this.onShelfResizeMove,
          },
        })
        return interactable
      })
    },
    /**
     * 释放当前页面创建的拖动实例，避免筛选、区域切换或页面销毁后重复监听。
     */
    destroyShelfDragging() {
      this.shelfInteractables.forEach((interactable) => interactable.unset())
      this.shelfInteractables = []
    },
    /**
     * 根据 DOM 上的货架标识查找当前区域对应的货架对象。
     * @param {HTMLElement} element 当前被拖动的货架元素
     * @returns {Object|undefined} 当前货架数据
     */
    findShelfByElement(element) {
      const shelfKey = element.dataset.shelfKey
      return (this.warehouseData[this.i] || []).find((shelf) => this.getShelfKey(shelf) == shelfKey)
    },
    /**
     * 开始拖动时将当前货架提升到最上层，确保重叠后仍能看到正在操作的货架。
     * @param {Object} event interact.js 拖动事件
     */
    onShelfDragStart(event) {
      const shelf = this.findShelfByElement(event.target)
      if (!shelf) return
      const maxZIndex = (this.warehouseData[this.i] || []).reduce((max, item) => {
        return Math.max(max, item._featureLayout ? item._featureLayout.zIndex : 0)
      }, 0)
      this.$set(shelf._featureLayout, 'zIndex', maxZIndex + 1)
    },
    /**
     * 拖动过程中累加横纵坐标，Vue 会立即更新货架的 transform 位置。
     * @param {Object} event interact.js 拖动事件，dx、dy 为本次移动距离
     */
    onShelfDragMove(event) {
      const shelf = this.findShelfByElement(event.target)
      if (!shelf) return
      this.$set(shelf._featureLayout, 'x', shelf._featureLayout.x + event.dx)
      this.$set(shelf._featureLayout, 'y', shelf._featureLayout.y + event.dy)
    },
    /**
     * 编辑模式下通过货架右边缘调整宽度，宽度最小保持 506px。
     * 只调整右边缘，因此缩放不会改变货架当前的 x、y 坐标。
     * @param {Object} event interact.js 缩放事件，rect.width 为缩放后的货架宽度
     */
    onShelfResizeMove(event) {
      const shelf = this.findShelfByElement(event.target)
      if (!shelf) return
      this.$set(shelf._featureLayout, 'width', Math.max(506, event.rect.width))
    },
    /**
     * 将指定区域全部货架的坐标、宽度和层级保存到本地，只保存布局信息，不缓存库存数据。
     * @param {String|Number} area 区域标识
     */
    saveShelfLayout(area) {
      const positions = {}
      const shelves = this.warehouseData[area] || []
      shelves.forEach((shelf) => {
        positions[this.getShelfKey(shelf)] = {
          x: shelf._featureLayout.x,
          y: shelf._featureLayout.y,
          width: Math.max(506, shelf._featureLayout.width || 506),
          zIndex: shelf._featureLayout.zIndex,
        }
      })
      localStorage.setItem(this.getShelfLayoutStorageKey(area), JSON.stringify({
        version: 1,
        positions,
      }))
      this.$set(this.customLayoutAreas, String(area), true)
    },
  },
}
